'use client';

/**
 * GuestV2MenuItemSheet — the Chow mobile item view, on the web.
 *
 * This is the add-to-cart surface in V2. Guests can add items directly from
 * this sheet with notes and customization.
 *
 * @location components/place/GuestV2MenuItemSheet.tsx
 */

import Image from 'next/image';
import { useEffect, useState, type CSSProperties } from 'react';
import {
  AlertTriangle,
  Coffee,
  Info,
  Minus,
  Plus,
  ShieldAlert,
  SlidersHorizontal,
  X,
} from 'lucide-react';

import { ItemKindDetails } from '@/components/menu/ItemKindDetails';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/components/ui/utils';
import { assessmentFromItem } from '@/lib/menu/allergens';
import { availabilityReasonCopy } from '@/lib/menu/availability';
import { describeItemDietary, EMPTY_DIETARY_PROFILE, type DietaryProfile } from '@/lib/menu/dietary';
import { formatItemPrice, formatOrderMoney, getItemNumericPrice, priceUnitSuffix } from '@/lib/menu/ordering/utils';
import type { OrderingMenuItem } from '@/lib/menu/ordering/types';

type Props = {
  item: OrderingMenuItem & Record<string, unknown> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd?: (quantity: number, note: string) => void;
  onOrderNow?: (quantity: number, note: string) => void;
  addLabel?: string;
  orderNowLabel?: string;
  unavailableHint?: string | null;
  dietaryProfile?: DietaryProfile | null;
  canOrder?: boolean;
  paletteStyle?: CSSProperties;
};

export function GuestV2MenuItemSheet({
  item,
  open,
  onOpenChange,
  onAdd,
  onOrderNow,
  addLabel = 'Add to cart',
  orderNowLabel = 'Order now',
  unavailableHint,
  dietaryProfile = null,
  canOrder: canOrderProp,
  paletteStyle,
}: Props) {
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const resolvedCanOrder = canOrderProp !== undefined ? canOrderProp : Boolean(onAdd || onOrderNow);

  useEffect(() => {
    if (open) {
      setQuantity(1);
      setNote('');
    }
  }, [open, item?.id]);

  const verdict = item
    ? describeItemDietary(item, dietaryProfile ?? EMPTY_DIETARY_PROFILE)
    : { clash: false, note: null };
  const assessment = item ? assessmentFromItem(item) : 'not_assessed';
  const kind = item?.kind ? item.kind : null;
  const optionGroups = item?.option_groups ?? [];
  const requiredGroups = optionGroups.filter((group) => group.required).length;
  const availability = item
    ? availabilityReasonCopy(item.availability_verdict ?? { available: true })
    : null;
  const canOrder = Boolean(onAdd);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className={cn(
          'chow-root isolate flex max-h-[94svh] w-full !max-w-none flex-col gap-0 overflow-hidden',
          // The shared SheetContent appends its own top-right close button.
          // Chow's item screen has one control, a back/close at the top left.
          '[&>button.absolute]:hidden',
          'rounded-t-[32px] border-x-0 border-b-0 border-t border-[hsl(var(--chow-rule))] p-0',
          'text-[hsl(var(--chow-ink))] shadow-[0_-18px_54px_rgba(0,0,0,0.22)]',
          'md:!left-1/2 md:!right-auto md:w-[min(94vw,42rem)] md:-translate-x-1/2 md:rounded-[28px] md:border',
        )}
        style={{
          ...paletteStyle,
          background: 'hsl(var(--chow-paper))',
          color: 'hsl(var(--chow-ink))',
          minHeight: 0,
          paddingBottom: 0,
        }}
        overlayStyle={{ backgroundColor: 'rgba(0,0,0,0.42)' }}
      >
        {item ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 flex-col items-center px-4 pt-3">
              <div className="mb-2 h-1.5 w-10 rounded-full bg-[hsl(var(--chow-rule))]" aria-hidden="true" />
              <div className="flex w-full items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  aria-label="Close"
                  className="gv-icon-btn"
                >
                  <X aria-hidden="true" className="size-5" />
                </button>

                {kind && kind !== 'dish' ? (
                  <span className="rounded-full bg-[hsl(var(--chow-sunk))] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[hsl(var(--chow-soft))]">
                    {kind.replaceAll('_', ' ')}
                  </span>
                ) : (
                  <div className="size-11" aria-hidden="true" />
                )}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-8">
              <div className="mx-auto mt-4 aspect-square w-[min(72vw,280px)] max-w-full overflow-hidden rounded-full border-[6px] border-[hsl(var(--chow-paper))] bg-[hsl(var(--chow-accent-wash))] shadow-[0_18px_40px_-24px_hsl(var(--chow-shadow)/0.45)]">
                {item.image ? (
                  <div className="relative size-full">
                    <Image
                      src={item.image}
                      alt=""
                      fill
                      unoptimized
                      sizes="280px"
                      className="object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <span className="grid size-full place-items-center text-[hsl(var(--chow-soft))]">
                    <Coffee aria-hidden="true" className="size-14" />
                  </span>
                )}
              </div>

              <div className="mt-5 flex items-start gap-3">
                <SheetHeader className="min-w-0 flex-1 p-0 text-left">
                  <SheetTitle className="font-[family-name:var(--font-chow-display)] text-[26px] font-semibold leading-[34px] tracking-[-0.02em] text-[hsl(var(--chow-ink))]">
                    {item.title || item.name || 'Menu item'}
                  </SheetTitle>
                  <SheetDescription className="sr-only">{resolvedCanOrder ? 'Choose a quantity and add this item to your cart. You can review your order before placing it.' : 'View item details and ask the restaurant if you need more information.'}</SheetDescription>
                </SheetHeader>

                {(item.price !== undefined || item.price_value !== undefined) && (
                  <span className="shrink-0 pt-0.5 text-[22px] font-semibold leading-[30px] tracking-[-0.02em] tabular-nums text-[hsl(var(--primary))]">
                    {formatItemPrice(item)}
                    {priceUnitSuffix(item) ? (
                      <span className="ml-1 text-[11px] font-normal text-[hsl(var(--chow-soft))]">
                        {priceUnitSuffix(item)}
                      </span>
                    ) : null}
                  </span>
                )}
              </div>

              {item.description?.trim() ? (
                <p className="mt-2 whitespace-pre-line text-[15px] leading-[23px] text-[hsl(var(--chow-soft))]">
                  {item.description.trim()}
                </p>
              ) : null}

              {verdict.note ? (
                <div
                  className={cn(
                    'mt-4 flex items-start gap-2 rounded-2xl px-3.5 py-3 text-[13px] leading-5',
                    verdict.clash
                      ? 'bg-[color-mix(in_srgb,hsl(var(--chow-ember))_10%,transparent)] text-[hsl(var(--chow-ember))]'
                      : 'bg-[hsl(var(--chow-sunk))] text-[hsl(var(--chow-soft))]',
                  )}
                >
                  {verdict.clash ? (
                    <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                  ) : (
                    <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="font-medium">{verdict.note}</p>
                    {verdict.clash ? (
                      <p className="mt-0.5 text-[12px] leading-5">
                        Based on your dietary needs. You can still order it.
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {availability ? (
                <p className="mt-3 flex items-start gap-2 text-[13px] leading-5 text-[hsl(var(--chow-ember))]">
                  <ShieldAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                  {availability}
                </p>
              ) : null}

              {(() => {
                const dietaryTags = item.dietary_tags ?? [];
                const allergens = item.allergens ?? [];
                const unconfirmed = item.unconfirmed_derived_allergens ?? [];
                const showAllergens = assessment === 'declared' && allergens.length > 0;
                if (!dietaryTags.length && !showAllergens && assessment !== 'not_assessed' && !unconfirmed.length) {
                  return null;
                }
                return (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {dietaryTags.map((tag) => (
                      <span
                        key={`diet-${tag}`}
                        className="rounded-full bg-[color-mix(in_srgb,hsl(var(--chow-leaf))_12%,transparent)] px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--chow-leaf))]"
                      >
                        {tag}
                      </span>
                    ))}
                    {assessment === 'not_assessed' ? (
                      <span className="rounded-full bg-[hsl(var(--chow-sunk))] px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--chow-soft))]">
                        Allergens not listed: ask the restaurant
                      </span>
                    ) : null}
                    {showAllergens ? (
                      <span className="rounded-full bg-[hsl(var(--chow-sunk))] px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--chow-soft))]">
                        Contains: {allergens.join(', ')}
                      </span>
                    ) : assessment === 'none_present' ? (
                      <span className="rounded-full bg-[color-mix(in_srgb,hsl(var(--chow-leaf))_12%,transparent)] px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--chow-leaf))]">
                        No known allergens
                      </span>
                    ) : null}
                    {unconfirmed.length ? (
                      <span className="rounded-full bg-[hsl(var(--chow-sunk))] px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--chow-soft))]">
                        May contain: {unconfirmed.join(', ')} (unconfirmed)
                      </span>
                    ) : null}
                  </div>
                );
              })()}

              {optionGroups.length ? (
                <section className="mt-5">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal aria-hidden="true" className="size-4 text-[hsl(var(--chow-soft))]" />
                    <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--chow-soft))]">
                      Make it yours
                    </h2>
                  </div>
                  <div className="mt-2 overflow-hidden rounded-2xl border border-[hsl(var(--chow-rule))] bg-[hsl(var(--chow-paper))]">
                    {optionGroups.map((group) => (
                      <div
                        key={group.id}
                        className="flex min-h-[48px] items-center justify-between gap-3 border-b border-[hsl(var(--chow-rule))] px-3.5 py-2.5 last:border-b-0"
                      >
                        <span className="min-w-0 truncate text-[14px] font-medium text-[hsl(var(--chow-ink))]">
                          {group.name}
                        </span>
                        <span
                          className={cn(
                            'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]',
                            group.required
                              ? 'bg-[color-mix(in_srgb,hsl(var(--primary))_12%,transparent)] text-[hsl(var(--primary))]'
                              : 'bg-[hsl(var(--chow-sunk))] text-[hsl(var(--chow-soft))]',
                          )}
                        >
                          {group.required ? 'Required' : 'Optional'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[12px] leading-5 text-[hsl(var(--chow-soft))]">
                    {requiredGroups > 0
                      ? `${requiredGroups} required choice${requiredGroups === 1 ? '' : 's'} · add them when ordering.`
                      : 'Options and add-ons available.'}
                  </p>
                </section>
              ) : null}

              {item.pricing_mode === 'per_unit_weight' ? (
                <div className="mt-4 rounded-2xl bg-[hsl(var(--chow-sunk))] px-3.5 py-3 text-[13px] leading-5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[hsl(var(--chow-soft))]">Priced by weight</span>
                    <span className="font-semibold tabular-nums text-[hsl(var(--chow-ink))]">
                      {item.price_per_unit_weight
                        ? `${formatOrderMoney(Number(item.price_per_unit_weight), item.currency)} / ${item.weight_unit || 'kg'}`
                        : '—'}
                    </span>
                  </div>
                  {item.min_weight || item.max_weight ? (
                    <p className="mt-1 text-[12px] text-[hsl(var(--chow-soft))]">
                      {item.min_weight || 0}–{item.max_weight || '∞'} {item.weight_unit || 'kg'}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {kind && kind !== 'dish' ? (
                <div className="mt-4 rounded-2xl bg-[hsl(var(--chow-sunk))] p-3.5">
                  <ItemKindDetails item={item} />
                </div>
              ) : null}

              {resolvedCanOrder ? (
                <div className="mt-5 space-y-2">
                  <label htmlFor="browse-item-instructions" className="text-sm font-semibold">Special instructions</label>
                  <p className="text-xs text-[hsl(var(--chow-soft))]">Optional, e.g. no onions, extra sauce</p>
                  <Textarea id="browse-item-instructions" value={note} onChange={(event) => setNote(event.target.value)} maxLength={240} placeholder="Add a note for this item…" className="min-h-[76px] resize-none" />
                </div>
              ) : null}
            </div>

            <div className="shrink-0 border-t border-[hsl(var(--chow-rule))] bg-[hsl(var(--chow-paper))] px-4 pb-[calc(16px+var(--chow-safe-bottom))] pt-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center rounded-full border border-[hsl(var(--chow-rule))] bg-[hsl(var(--chow-sunk))]/40 p-0.5">
                  <button
                    type="button"
                    className="flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-[hsl(var(--chow-paper))] disabled:opacity-40"
                    aria-label="Decrease quantity"
                    disabled={quantity <= 1}
                    onClick={() => setQuantity((v) => Math.max(1, v - 1))}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="min-w-[2ch] text-center text-[15px] font-semibold tabular-nums">{quantity}</span>
                  <button
                    type="button"
                    className="flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-[hsl(var(--chow-paper))] disabled:opacity-40"
                    aria-label="Increase quantity"
                    disabled={quantity >= 50}
                    onClick={() => setQuantity((v) => Math.min(50, v + 1))}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-[hsl(var(--chow-soft))]">Total</p>
                  <p className="text-[17px] font-semibold tabular-nums text-[hsl(var(--chow-ink))]">
                    {formatOrderMoney(getItemNumericPrice(item) * quantity, item.currency)}
                  </p>
                </div>
              </div>

              {resolvedCanOrder ? (
                onOrderNow ? (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => onAdd?.(quantity, note)}
                      className="flex h-12 items-center justify-center rounded-full border border-[hsl(var(--primary))] px-4 text-[15px] font-semibold text-[hsl(var(--primary))] transition active:scale-[0.98]"
                    >
                      {optionGroups.length ? 'Customize and add to cart' : addLabel}
                    </button>
                    <button
                      type="button"
                      onClick={() => onOrderNow?.(quantity, note)}
                      className="flex h-12 items-center justify-center rounded-full bg-[hsl(var(--primary))] px-4 text-[15px] font-semibold text-[var(--primary-foreground)] transition active:scale-[0.98]"
                    >
                      {orderNowLabel}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onAdd?.(quantity, note)}
                    className="mt-3 flex h-12 w-full items-center justify-center rounded-full bg-[hsl(var(--primary))] px-5 text-[16px] font-semibold text-[var(--primary-foreground)] transition active:scale-[0.98]"
                  >
                    {optionGroups.length ? 'Customize and add to cart' : addLabel}
                  </button>
                )
              ) : unavailableHint ? (
                <p className="mt-3 pb-1 text-center text-[12px] leading-5 text-[hsl(var(--chow-soft))]">
                  {unavailableHint}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
